import classnames from 'classnames'
import { FieldValidator, useField } from 'formik'
import React, { ComponentPropsWithoutRef, useCallback, useRef, useState } from 'react'

import Field from '../Field/Field'
import FieldDropdown from '../FieldDropdown/FieldDropdown'
import FieldError from '../FieldError/FieldError'

import styles from './TextArea.module.scss'

type TextAreaProps = {
  placeholder?: string
  label?: string
  hideLabel?: boolean
  omitErrorDisplay?: boolean
  children?: React.ReactNode
  dropdown?: React.ReactNode
  dropdownOpen?: boolean
  className?: string
  touched?: boolean
  error?: string
} & Omit<React.ComponentPropsWithoutRef<'textarea'>, 'children'>

const TextArea = React.memo(
  React.forwardRef(function TextArea(
    {
      placeholder,
      label,
      hideLabel,
      omitErrorDisplay,
      dropdown,
      dropdownOpen,
      className,
      touched,
      error,
      ...componentProps
    }: TextAreaProps,
    ref: React.ForwardedRef<HTMLTextAreaElement>
  ) {
    // Focus detection (for dropdown visibility)
    const [hasFocus, setHasFocus] = useState(false)

    const handleFocus = useCallback<React.FocusEventHandler<HTMLDivElement>>((e) => {
      setHasFocus(e.currentTarget.contains(e.target))
    }, [])
    const handleBlur = useCallback<React.FocusEventHandler<HTMLDivElement>>((e) => {
      setHasFocus(e.currentTarget.contains(e.relatedTarget))
    }, [])

    const fieldRef = useRef<HTMLDivElement | null>(null)

    return (
      <div onFocus={handleFocus} onBlur={handleBlur} className={classnames(styles.TextArea, className)}>
        <Field
          ref={fieldRef}
          label={hideLabel ? undefined : label}
          disabled={componentProps.disabled}
          hasError={Boolean(touched && error)}
          className={styles.field}
        >
          <div className={styles.container}>
            <textarea
              ref={ref}
              rows={10}
              placeholder={hideLabel ? placeholder || label : placeholder}
              aria-label={label}
              {...componentProps}
              className={styles.input}
            />
          </div>
        </Field>
        <FieldError error={error} touched={touched} omitErrorDisplay={omitErrorDisplay} />
        <FieldDropdown
          open={Boolean(dropdown) && (dropdownOpen !== undefined ? dropdownOpen : hasFocus)}
          anchor={fieldRef.current}
        >
          {dropdown}
        </FieldDropdown>
      </div>
    )
  })
)

export default TextArea

type TextAreaFormikProps = {
  name: string
  validate?: FieldValidator
} & ComponentPropsWithoutRef<typeof TextArea>

export const TextAreaFormik = React.memo(function TextAreaFormik({
  name,
  validate,
  ...componentProps
}: TextAreaFormikProps) {
  const inputRef = React.useRef<HTMLTextAreaElement>(null)

  const doValidate = useCallback(
    (value?: any) => {
      if (!inputRef.current?.validity.valid) {
        return inputRef.current?.validationMessage
      }
      if (validate) {
        return validate(value)
      }
    },
    [inputRef, validate]
  )

  const [field, meta, helpers] = useField({
    name,
    validate: doValidate,
    ...componentProps,
  })

  const value = field.value || ''

  const onChange = useCallback<React.ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => {
      return field.onChange(e)
    },
    [field.onChange, helpers.setValue]
  )

  return (
    <TextArea
      ref={inputRef}
      touched={meta.touched}
      error={meta.error}
      {...componentProps}
      {...field}
      value={value}
      onChange={onChange}
    />
  )
})
